import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { History as AuditIcon } from '@mui/icons-material';
import { adminApi } from '../../api';
import { useDocumentTitle, getStoredRowsPerPage, setStoredRowsPerPage } from '../../hooks/core';

interface AuditLog {
  id: string;
  userId: string;
  action: string;
  category: string;
  description: string;
  details: Record<string, unknown>;
  ipAddress: string;
  createdAt: string;
  user?: {
    id: string;
    email: string;
    displayName: string;
  };
}

const categories = [
  { value: '', label: 'All Categories' },
  { value: 'settings', label: 'Settings' },
  { value: 'products', label: 'Products' },
  { value: 'coupons', label: 'Coupons' },
  { value: 'credits', label: 'Credits' },
  { value: 'subscriptions', label: 'Subscriptions' },
];

export function AuditLogPage() {
  useDocumentTitle('Audit Log');

  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(() => getStoredRowsPerPage());
  const [categoryFilter, setCategoryFilter] = useState<string>('');

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const response = await adminApi.getAuditLogs({
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        category: categoryFilter || undefined,
      });
      setLogs(response.logs || []);
      setTotal(response.total || 0);
      setError(null);
    } catch (err) {
      setError('Failed to load audit logs');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, categoryFilter]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'settings':
        return 'primary';
      case 'products':
        return 'secondary';
      case 'coupons':
        return 'info';
      case 'credits':
        return 'success';
      case 'subscriptions':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getActionLabel = (action: string): string => {
    return action.split('_').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <AuditIcon color="primary" sx={{ fontSize: 32 }} />
        <Typography variant="h4">Audit Log</Typography>
      </Box>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        View payment-related administrative actions and changes.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Category</InputLabel>
          <Select
            value={categoryFilter}
            label="Category"
            onChange={(e) => {
              setCategoryFilter(e.target.value);
              setPage(0);
            }}
          >
            {categories.map((cat) => (
              <MenuItem key={cat.value} value={cat.value}>
                {cat.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Paper>

      <Paper>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Action</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>IP Address</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">
                          No audit logs found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    logs.map((log) => (
                      <TableRow key={log.id} hover>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(log.createdAt)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography fontWeight="medium">
                            {log.user?.displayName || 'System'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {log.user?.email}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={log.category}
                            size="small"
                            color={getCategoryColor(log.category) as 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'default'}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {getActionLabel(log.action)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ maxWidth: 300 }}>
                            {log.description}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {log.ipAddress || '-'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                const newSize = parseInt(e.target.value, 10);
                setRowsPerPage(newSize);
                setStoredRowsPerPage(newSize);
                setPage(0);
              }}
              rowsPerPageOptions={[10, 20, 50]}
            />
          </>
        )}
      </Paper>
    </Box>
  );
}
